import { useCallback, useMemo } from "react";

import moment from "moment";
import { useLocation } from "react-router-dom";

import { API_DATE_FORMAT } from "@/src/constants/dateFormat";

import useUpdateSearchParams from "./useUpdateSearchParams";

import type { Moment } from "moment";

export interface AnalyticsPeriod {
  startDate: Moment;
  endDate: Moment;
}

interface PeriodBounds {
  from: string;
  to: string;
}

interface AnalyticsPeriodState {
  period: AnalyticsPeriod;
  setPeriod: (startDate: Moment, endDate: Moment) => void;
}

export function toPeriod(from: string, to: string): AnalyticsPeriod {
  return {
    startDate: moment(from, API_DATE_FORMAT),
    endDate: moment(to, API_DATE_FORMAT),
  };
}

function isValidDay(value: string | null): value is string {
  return !!value && moment(value, API_DATE_FORMAT, true).isValid();
}

function getDefaultPeriodBounds(today: string): PeriodBounds {
  return {
    from: moment(today, API_DATE_FORMAT)
      .startOf("month")
      .format(API_DATE_FORMAT),
    to: today,
  };
}

export function parsePeriodParams(search: string, today: string): PeriodBounds {
  const params = new URLSearchParams(search);
  const from = params.get("from");
  const to = params.get("to");

  return isValidDay(from) && isValidDay(to) && from <= to
    ? { from, to }
    : getDefaultPeriodBounds(today);
}

/** Analytics period synced with `?from=&to=`. */
function usePeriodFilter(): AnalyticsPeriodState {
  const location = useLocation();
  const updateSearchParams = useUpdateSearchParams();
  const today = useMemo(() => moment().format(API_DATE_FORMAT), []);
  const { from, to } = useMemo(
    () => parsePeriodParams(location.search, today),
    [location.search, today],
  );

  // Moments are rebuilt from day strings only: they key React Query caches.
  const period = useMemo(() => toPeriod(from, to), [from, to]);

  const setPeriod = useCallback(
    (startDate: Moment, endDate: Moment): void => {
      updateSearchParams({
        from: startDate.format(API_DATE_FORMAT),
        to: endDate.format(API_DATE_FORMAT),
      });
    },
    [updateSearchParams],
  );

  return { period, setPeriod };
}

export default usePeriodFilter;
